import { useState, useEffect, useRef, MutableRefObject, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getVideoCalls, getCallingElevator, getVideoCallEvents } from "selectors/ecsSelectors";
import { WebRTC } from "../../ui/video/call/webrtc";
import { brokenVideoCall, sendEcsMSCallMessage, videoCallEnded, videoCallFailed } from "actions/ecsActions";
import { useWebSocket } from "hooks/useWebSocket/useWebSocket";
import { CallId, GroupId, VideoCallEvent, VideoCallRejectEvent } from 'shared/models';
import { State } from "reducers";
import moment from 'moment';
import UUID from "util/UUID";
import { toError } from 'shared/util/toError';

const PING_INTERVAL = 2000;

export type Message = {
    text: string;
    local: boolean;
    messageId: string;
    timestamp: number;
    acknowledged: boolean;
};

export type Props = {
    videoElementRef: MutableRefObject<HTMLVideoElement | null>;
};


export const useVideoSession = ({
    videoElementRef,
}: Props) => {
    const { t } = useTranslation();

    const log = (level: 'info' | 'warn' | 'error', ...args: any[]) => {
        args.unshift(`${moment.utc().format('H:mm:ss:SSS')} -`);
        if (callIdRef.current) {
            args.unshift(`[#${callIdRef.current}]`);
        }
        console[level].call(null, ...args);
    };
    
    const websocketClient = useWebSocket();

    const videoCalls = useSelector(getVideoCalls);

    const endedRef = useRef(false);
    const joinedRef = useRef(false);

    const callIdRef = useRef<CallId>(); // Use for places where we need to be immutable
    const [ callId, __setCallId ] = useState<CallId>(); // Use for places that need to change if the call ID changes

    const loadCallId = useCallback((callId: CallId): boolean => {
        if (callIdRef.current === callId) {
            return false;
        }
        callIdRef.current = callId;
        __setCallId(callId);
        return true;
    }, [ __setCallId, callIdRef ]);

    const [ elevatorId, setElevatorId ] = useState<GroupId>();

    const callingElevatorSince = useSelector((state: State) => getCallingElevator(state, { elevatorId }));

    const dispatch = useDispatch();

    const webrtcRef = useRef<WebRTC>();
    const [ helpIsOnTheWay, setHelpIsOnTheWay ] = useState(false);
    const [ chatHistory, setChatHistory ] = useState<Message[]>([]);
    const [ connected, setConnected ] = useState(false);
    const [ connecting, setConnecting ] = useState(false);
    const [ loadingVideo, setLoadingVideo ] = useState(false);
    const [ startVideoButtonVisible, setStartVideoButtonVisible ] = useState(false);
    const [ dataChannelConnected, setDataChannelConnected ] = useState(false);
    const [ errorText, setErrorText ] = useState<string | null>(null);
    const reconnectWindowExpiredTimerIdRef = useRef<NodeJS.Timer>();
    const reconnectTimerIdRef = useRef<NodeJS.Timeout>();
    const connectTimerIdRef = useRef<NodeJS.Timeout>();
    const pingIntervalRef = useRef<number>();

    const events = useSelector((state: State) => callId ? getVideoCallEvents(state, { callId }) : []) as VideoCallEvent[];

    const clearError = useCallback(() => {
        setErrorText(oldText => {
            if (oldText) {
                log('info', 'Clearing error text');
            }
            return null;
        });
    }, [ setErrorText ]);

    const setError = useCallback((key: string, fatal: boolean): boolean => {
        let callJustEnded = false;
        const text = t(`VIDEO.ERROR.${key}`);
        log(fatal ? 'error' : 'warn', `Setting error text: ${text}`);

        setErrorText(text);

        if (fatal) {
            callJustEnded = !endedRef.current; // return true if this call to setError resulted in the call ending
            endedRef.current = true;
            setConnected(false);
            setConnecting(false);
            setLoadingVideo(false);
            setDataChannelConnected(false);
            clearTimeout(connectTimerIdRef.current!);
            clearInterval(pingIntervalRef.current!);
            
            if (callIdRef.current && callJustEnded) {
                websocketClient.ecsGoodbye(callIdRef.current);
            }
        }

        return callJustEnded;
    }, [ endedRef, callIdRef, websocketClient, setErrorText, setConnected, setConnecting, setLoadingVideo, setDataChannelConnected, t, connectTimerIdRef ]);

    const addMessage = useCallback((text: string, local: boolean, messageId: string) => {
        setChatHistory(history => [
            ...history,
            {
                text,
                local,
                messageId,
                timestamp: Date.now(),
                acknowledged: false,
            },
        ]);
    }, [ setChatHistory ]);

    //
    // Handle the case where we request to join a call but the Gateway rejects us
    // (e.g. another operator got to the call first or a Lobby Panel is connected
    // to the elevator or the call was unsolicited and the building is configured
    // to block unsolicited calls)
    //
    const handleRejection = useCallback((callId: string, reason: number) => {
        log('warn', `Call rejected`);
        endedRef.current = true; // to prevent the assignment of a callId to start a call
        loadCallId(callId);
        setError(`ECS_CALL_REJECTED_${reason}`, true);
        dispatch(videoCallFailed(callId));
    }, [ endedRef, loadCallId, setError, dispatch ]);

    //
    // Handle the case where a Lobby Panel connected to the call and has
    // booted us off of the call.
    //
    const handleOverride = useCallback((overriddenCallId: string) => {
        log('info', overriddenCallId, `Call overridden by lobby panel`);
        setError(`ECS_CALL_OVERRIDE`, true);
        dispatch(videoCallEnded(overriddenCallId, { when: Date.now() }));
    }, [ setError, dispatch ]);

    const handleReadReceipt = useCallback((messageId: string) => {
        setChatHistory(history => {
            let changed = false;
            const newHistory = history.map(msg => {
                if (msg.messageId === messageId && !msg.acknowledged) {
                    changed = true;
                    return {
                        ...msg,
                        acknowledged: true,
                    };
                }
                return msg;
            });
            return changed ? newHistory : history;
        });
    }, [ setChatHistory ]);

    //
    // We use this event to know that the video has started playing
    //
    const handleVideoTimeUpdate = useCallback(() => {
        const videoElement = videoElementRef.current;
        if (videoElement && videoElement.currentTime > 0) {
            videoElement.ontimeupdate = null;
            setStartVideoButtonVisible(false);
            setLoadingVideo(false);
            clearError();
        }
    }, [ videoElementRef, setStartVideoButtonVisible, setLoadingVideo, clearError ]);

    //
    // We will try to play video right away after connecting to the video source...
    // but sometimes the client browser might refuse to play video right away
    // (e.g. if the user hasn't interacted with the page before and they haven't
    // enabled the Chrome flag to bypass that "feature"). So we also allow the
    // page to call playVideo directly in response to the user clicking a button.
    //
    const playVideo = useCallback(async () => {
        const videoElement = videoElementRef.current;
        if (!videoElement) {
            log('warn', 'Oops... video element is null');
            return;
        }
        if (endedRef.current) {
            log('warn', 'Cannot start to play because call has ended');
            return;
        }
        setLoadingVideo(true);
        setStartVideoButtonVisible(false);
        try {
            log('info', 'Requesting to play video');
            const start = performance.now();
            await videoElement.play();
            if (endedRef.current) {
                log('warn', 'Cannot play video because call has ended');
                return;
            }
            const delta = Math.round(performance.now() - start);
            log('info', `Video playing after ${delta / 1000}s`);
            clearError();
        }
        catch (e) {
            const err = toError(e);
            log('error', 'Failed to play video:', err);
            if (err.name === 'NotAllowedError') {
                setStartVideoButtonVisible(true);
                return;
            }
            setError('VIDEO_FAILED', true);
        }
    }, [ videoElementRef, endedRef, setLoadingVideo, setError, clearError, setStartVideoButtonVisible ]);

    const handleConnectVideo = useCallback(async (stream: MediaStream) => {
        log('info', 'Received stream', stream);
        const videoElement = videoElementRef.current;
        if (!videoElement) {
            log('warn', 'Oops... video element is null');
            return;
        }
        if (endedRef.current) {
            log('warn', 'Cannot connect video because call has ended');
            return;
        }
        setConnected(true);
        clearTimeout(connectTimerIdRef.current!);
        
        setConnecting(false);
        setLoadingVideo(true);
        try {
            videoElement.ontimeupdate = handleVideoTimeUpdate;
            videoElement.srcObject = stream;
            await playVideo();
        }
        catch (err) {
            log('error', 'Failed to connect video:', err);
            setError('VIDEO_FAILED', true);
        }
    }, [ videoElementRef, endedRef, setConnected, setConnecting, setLoadingVideo, setError, handleVideoTimeUpdate, playVideo ]);

    const handleConnectData = useCallback(() => {
        log('info', 'Data channel opened');
        setDataChannelConnected(true);
    }, [ setDataChannelConnected ]);

    const handleLocalIceCandidate = useCallback((offerSeq: number, candidate: RTCIceCandidate) => {
        if (endedRef.current) {
            log('warn', 'Ignoring locale ice candidate because call has ended');
            return;
        }
        websocketClient.ecsIceCandidate(callIdRef.current!, candidate ? [ JSON.stringify(candidate) ] : [], offerSeq, !candidate);
    }, [ websocketClient, endedRef, callIdRef ]);

    const handleMuted = useCallback(() => {
        log('warn', 'video muted');
        setLoadingVideo(true);
    }, [ setLoadingVideo ]);

    const handleUnmuted = useCallback(() => {
        log('warn', 'video unmuted');
        const videoElement = videoElementRef.current;
        if (videoElement) {
            videoElement.ontimeupdate = handleVideoTimeUpdate;
        }
    }, [ videoElementRef, handleVideoTimeUpdate ]);

    // Data has arrived from the elevator
    const handleDataReceived = useCallback((jsonPayload: string) => {
        try {
            const obj = JSON.parse(jsonPayload);
            
            if (obj.type === 'RREC') {
                handleReadReceipt(obj.messageId);
                return;
            }

            if (obj.message === 'YES:DOWN') {
                addMessage('YES', false, '');
            }
            else if (obj.message === 'NO:DOWN') {
                addMessage('NO', false, '');
            }
        }
        catch (err) {
            log('error', `Failed to parse incoming chat data: ${jsonPayload}`, err);
        }
    }, [ addMessage, handleReadReceipt ]);

    const sendHelpIsOnTheWay = useCallback((value: boolean) => {
        if (!webrtcRef.current) {
            return;
        }
        try {
            if (value) {
                webrtcRef.current.sendHelpIsOnTheWay();            // Tell the elevator that help is coming
                websocketClient.ecsHelpComing(callIdRef.current!); // Tell MosaicONE that we said help is coming
                setHelpIsOnTheWay(true);                           // Update the UI to show we said help is coming
            }
        }
        catch (err) {
            log('error', 'Failed to send Help is on the way:', err);
            setError('HELP_STATUS_FAILED', false);
        }
    }, [ webrtcRef, callIdRef, setHelpIsOnTheWay, setError, websocketClient ]);

    const sendChatMessage = useCallback((text: string, doNotRecord: boolean = false): boolean => {
        if (!webrtcRef.current) {
            return false;
        }
        try {
            const messageId = UUID.generate();
            webrtcRef.current.sendMessage(text, messageId);
            clearError();
            addMessage(text, true, messageId);
            const recordedText = doNotRecord ? null : text;
            websocketClient.ecsChat(callIdRef.current!, recordedText, messageId);
            return true;
        }
        catch (err) {
            log('error', 'Failed to send chat message', err);
            setError('SEND_MESSAGE_FAILED', false);
            return false;
        }
    }, [ webrtcRef, callIdRef, setError, clearError, addMessage, websocketClient ]);

    const handleBrokenCall = useCallback(() => {
        if (setError('CONNECTION_LOST', true)) {
            if (elevatorId) {
                dispatch(brokenVideoCall({ elevatorId, ended: Date.now() }));
            }

            if (webrtcRef.current) {
                webrtcRef.current.removeAllListeners();
                webrtcRef.current.close();
                webrtcRef.current = undefined;
            }
        }
    }, [ elevatorId, setError, dispatch, webrtcRef ]);

    const handleDisconnected = useCallback(() => {
        // Connection has been interrupted... need to reconnect with elevator
        // WebRTC should take try on its own
        log('warn', 'Connection has been lost');
    }, []);

    const handleIceFailed = useCallback(() => {
        log('error', 'The ICE connection has failed');
        handleBrokenCall();
    }, [ handleBrokenCall ]);

    const handleCallFailed = useCallback(() => {
        log('error', 'The call has failed');
        handleBrokenCall();
    }, [ handleBrokenCall ]);


    const handleOffer = useCallback(async (callId: CallId, offer: string, seq: number, turnUsername?: string, turnPassword?: string) => {
        joinedRef.current = true;

        if (endedRef.current) {
            log('warn', 'Received offer but call has ended');
            websocketClient.ecsGoodbye(callId);
            dispatch(videoCallEnded(callId, { when: Date.now() }));
            return;
        }

        if (seq === 0) {
            clearTimeout(connectTimerIdRef.current!);
            log('info', 'Initializing video');

            try {
                webrtcRef.current = new WebRTC(turnUsername!, turnPassword!);
                webrtcRef.current
                    .on('connect-video', handleConnectVideo)
                    .on('connect-data', handleConnectData)
                    .on('disconnected', handleDisconnected)
                    .on('data', handleDataReceived)
                    .on('iceCandidate', candidate => handleLocalIceCandidate(seq, candidate))
                    .on('muted', handleMuted)
                    .on('unmuted', handleUnmuted)
                    .on('failed', handleCallFailed)
                    .on('ice-failed', handleIceFailed)
                ;
            }
            catch (e) {
                const err = toError(e);
                setError(err.message, true);
                return;
            }
        }
        // else TODO: renegotiation (will also need to update the iceCandidate handler to use the correct seq)

        if (!webrtcRef.current) {
            return;
        }

        try {
            const answer = await webrtcRef.current.negotiateSession(offer);

            if (!webrtcRef.current) {
                log('warn', 'Abandoned previous connection attempt');
                return;
            }
            if (endedRef.current) {
                log('warn', 'Prepared answer but call has ended');
                return;
            }
    
            websocketClient.ecsAnswer(callId, JSON.stringify(answer), seq);
        }
        catch (err) {
            log('error', 'Failed to negotiate video session:', err);
            setError('VIDEO_FAILED', true);
        }

        // Ping the server for the duration of the call because
        // sometimes the Goodbye message doesn't get sent/received.
        pingIntervalRef.current = window.setInterval(() => websocketClient.ecsPing(callId), PING_INTERVAL);
    }, [ webrtcRef, joinedRef, endedRef, setError, websocketClient, pingIntervalRef, handleCallFailed, handleConnectData, handleConnectVideo, handleDataReceived, handleDisconnected, handleIceFailed, handleLocalIceCandidate, handleMuted, handleUnmuted, dispatch ]);

    const handleMessage = useCallback((msg: any) => {
        if (msg.scope !== 'ECS') return;
        switch (msg.type) {
            case 'OFFR':
                void handleOffer(msg.callId, msg.offer, msg.seq, msg.turnUsername, msg.turnPassword);
                break;

            case 'RJCT':
                handleRejection(msg.callId, msg.reason);
                break;

            case 'ICEE':
                if (webrtcRef.current) {
                    const candidates = (msg.candidates as string[]).map(c => c && JSON.parse(c));
                    void webrtcRef.current.addRemoteCandidates(candidates);
                }
                break;

            case 'OVRD':
                handleOverride(msg.callId);
                break;

            case 'RREC':
                handleReadReceipt(msg.messageId);
                break;
        }
    }, [ webrtcRef, handleOffer, handleRejection, handleOverride, handleReadReceipt ]);

    const endCall = useCallback((callId: CallId) => {
        clearTimeout(connectTimerIdRef.current!);

        if (!callId) {
            log('warn', 'Attempt to end call with an empty ID');
            return;
        }

        // Bail out if the call has already been ended
        if (endedRef.current) {
            return;
        }
        endedRef.current = true;

        log('info', 'Ending call');
        websocketClient.ecsGoodbye(callId);

        // Force our local copy of the call to appear ended before we receive
        // notification from the server. This helps in the strange case where
        // we have a race condition when the browser is closed. Somehow, we
        // end up making a MSCL request right after the goodbye is sent.
        // I think the solution is to prevent the client from thinking there
        // is still a pending call that needs to be answered.
        if (joinedRef.current) {
            dispatch(videoCallEnded(callId, { when: Date.now() }));
        }
        else {
            dispatch(videoCallFailed(callId));
        }

        setHelpIsOnTheWay(false);
        setChatHistory([]);
        setConnected(false);
        setConnecting(false);
        setLoadingVideo(false);
        setDataChannelConnected(false);
        clearError();

        if (webrtcRef.current) {
            webrtcRef.current.removeAllListeners();
            webrtcRef.current.close();
            webrtcRef.current = undefined;
        }
    }, [ webrtcRef, websocketClient, endedRef, setHelpIsOnTheWay, setChatHistory, setConnected, setConnecting, setLoadingVideo, setDataChannelConnected, clearError, dispatch ]);

    //
    // Register to receive messages from the websocket
    //
    useEffect(() => {
        websocketClient.on('message', handleMessage);

        return () => {
            websocketClient.off('message', handleMessage);
        };
    }, [ websocketClient, handleMessage ]);

    //
    // Attempt to report the end of a call if the user closes the window
    // before the call has ended.
    //
    useEffect(() => {
        if (callId && (connected || connecting)) {
            window.onbeforeunload = () => {
                endCall(callId);
            };
        
            return () => {
                window.onbeforeunload = null;
            };
        }
    }, [ websocketClient, callId, connected, connecting, endCall ]);

    const handleConnectTimeout = useCallback((callId: CallId) => {
        log('warn', 'Connection timeout');
        setError('ECS_CALL_TIMEOUT', true);
        dispatch(videoCallFailed(callId!));
    }, [ setError, dispatch ]);

    const connectToCall = useCallback((callId: CallId) => {
        if (endedRef.current) {
            log('warn', 'Cannot connect ended call');
            setError('SESSION_NOT_FOUND', true);
            dispatch(videoCallFailed(callId));
            return;
        }
        const videoCall = videoCalls[callId];
        if (!videoCall) return;
        if (videoCall.ended || videoCall.joined) {
            setError('SESSION_NOT_FOUND', true);
            dispatch(videoCallFailed(callId));
            return;
        }

        if (loadCallId(callId)) {
            setElevatorId(videoCall.elevatorId);
            log('info', `Connecting to elevator ${videoCall.elevatorId}`);
            setConnecting(true);
    
            if (videoCall.source === 'EL' && !callingElevatorSince) {
                dispatch(sendEcsMSCallMessage(videoCall.elevatorId, { sent: Date.now() }));
                websocketClient.ecsCall(videoCall.elevatorId, true);
            }

            connectTimerIdRef.current = setTimeout(() => handleConnectTimeout(callId), 30 * 1000);
        }
    }, [ dispatch, loadCallId, setElevatorId, setConnecting, setError, handleConnectTimeout, connectTimerIdRef, videoCalls, endedRef, callingElevatorSince, websocketClient ]);

    // Clean up WebRTC and various timers when the video session is unmounted
    useEffect(() => {
        const videoElement = videoElementRef.current;
        
        return () => {
            const webrtc = webrtcRef.current;
            if (webrtc) {
                log('info', 'Cleaning up WebRTC');
                webrtc.close();
                webrtc.removeAllListeners();
                webrtcRef.current = undefined;
            }

            if (videoElement) {
                videoElement.ontimeupdate = null;
            }
    
            clearTimeout(reconnectWindowExpiredTimerIdRef.current!);
            clearInterval(reconnectTimerIdRef.current!);
            clearInterval(pingIntervalRef.current!);
        };
    }, [ webrtcRef, videoElementRef, reconnectWindowExpiredTimerIdRef, reconnectTimerIdRef, pingIntervalRef ]);

    // Begin a call when we have a device ID and aren't already calling for that device
    useEffect(() => {
        if (endedRef.current) return; // Cannot resurrect a dead call
        if (!callId) return;
        const videoCall = videoCalls[callId];

        if (elevatorId && !connecting && !connected && !loadingVideo && (!videoCall || !videoCall.joined)) {
            setConnecting(true);

            if (videoCall && videoCall.source === 'EL' && !callingElevatorSince) {
                dispatch(sendEcsMSCallMessage(elevatorId, { sent: Date.now() }));

                websocketClient.ecsCall(elevatorId, true);
            }

            connectTimerIdRef.current = setTimeout(() => handleConnectTimeout(callId), 30 * 1000);
        }
    }, [ elevatorId, callId, callingElevatorSince, endedRef, setConnecting, connecting, connected, loadingVideo, videoCalls, websocketClient, dispatch, connectTimerIdRef, handleConnectTimeout ]);

    // Reset the failed flag if the video element changes
    // (i.e. user left the old chat page and went to a new
    // chat page)
    useEffect(() => {
        if (videoElementRef.current) {
            endedRef.current = false;
        }
    }, [ videoElementRef, endedRef ]);

    const rejectionReason =
        (events || []).filter(event => event.type === 'RJCT')
            .map(event => (event as VideoCallRejectEvent).details.reason)
            .shift();

    // Was a call initiated elsewhere (e.g. an unsolicited call or a callback) rejected
    // before we had a chance to set up our websocket listener? Then manually handle the
    // rejection.
    useEffect(() => {
        if (callId && !endedRef.current && rejectionReason !== undefined) {
            handleRejection(callId, rejectionReason);
        }
    }, [ callId, endedRef, rejectionReason, handleRejection ]);


    return {
        failed: endedRef.current,
        callId,
        connected,
        connecting,
        startVideoButtonVisible,
        canSendMessages: dataChannelConnected && !endedRef.current,
        loadingVideo,
        errorText,
        helpIsOnTheWay,
        chatHistory,
        sendHelpIsOnTheWay,
        sendChatMessage,
        connectToCall,
        playVideo,
        endCall,
        dismissError: clearError,
    };
};
